import React, { FC, useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { JsonPanel } from '../panel/json-panel';
import { FileProperties } from '../../models/file.models';
import {
  cborToHex,
  cborToText,
  documentLoader,
  getClearFileName, getJsonBytesSize,
  jsonToText
} from '../../utils/utils';
import { CborPanel } from '../panel/cbor-panel';
// @ts-ignore
import { encode, decode } from '@digitalbazaar/cborld';
import { toast } from 'react-toastify';

interface PanelsContainerProps {
  setStepHandle: (step: number) => void;
}

export const markerFrom = 'from';
export const markerTo = 'to';

export const PanelsContainer: FC<PanelsContainerProps> = ({ setStepHandle }) => {
  const [jsonText, setJsonText] = useState<string>('');
  const [jsonEdited, setJsonEdited] = useState<boolean>(false);
  const [jsonProperties, setJsonProperties] = useState<FileProperties>();

  const [cborText, setCborText] = useState<string>('');
  const [cborHex, setCborHex] = useState<string>('');
  const [cborBinary, setCborBinary] = useState<Uint8Array>(new Uint8Array());
  const [cborProperties, setCborProperties] = useState<FileProperties>();

  const [conversionRatio, setConversionRatio] = useState<string>();
  const [conversionTime, setConversionTime] = useState<string>();
  const [allStepsFinished, setAllStepsFinished] = useState<boolean>(false);

  const initJson = (content: string, properties: FileProperties) => {
    try {
      setJsonText(jsonToText(JSON.parse(content)));
      setJsonProperties(properties);
      setCborBinary(new Uint8Array())
      setCborHex('');
      setCborText('');
      setCborProperties(undefined);
      setStepHandle(1);
    } catch (e) {
      toast.error(String(e));;
    }
  }

  const initCbor = async (content: ArrayBuffer, properties: FileProperties) => {
    const newBinary = new Uint8Array(content);
    setCborBinary(newBinary)
    setCborHex(cborToHex(newBinary));
    setCborText((await cborToText(newBinary)));
    setCborProperties(properties);
    setJsonText('');
    setJsonProperties(undefined);
    setStepHandle(1);
  }

  useEffect(() => {
    if (jsonProperties && cborProperties) {
      const conversionRatio = (cborProperties.size / jsonProperties.size * 100).toFixed(2);
      setConversionRatio(conversionRatio);
    } else {
      setConversionRatio(undefined);
    }
  }, [cborProperties, jsonProperties])

  useEffect(() => {
    if (jsonText && cborText) {
      const conversionTime = performance.measure('Performance measurement', markerFrom, markerTo);
      console.log(conversionTime);
      setConversionTime((conversionTime.duration / 1000).toFixed(4));
    } else {
      setConversionTime(undefined);
    }
  }, [jsonText, cborText])

  useEffect(() => {
    if (jsonEdited && jsonText && cborText && !allStepsFinished) {
      setStepHandle(3)
      setAllStepsFinished(true);
    }
  }, [jsonEdited, jsonText, cborText])

  // @content property must be present to enable the conversion
  // It can be either url or json - we detect which one is it and apply it
  const jsonToCbor = async (file: string) => {
    try {
      const jsonldDocument = JSON.parse(file);
      performance.mark(markerFrom);
      const cborBinary: Uint8Array = await encode({ jsonldDocument, documentLoader });
      performance.mark(markerTo);
      const fileName = jsonProperties?.name ? `${getClearFileName(jsonProperties.name)}.cborld` : 'example';

      setAllStepsFinished(false);
      setCborText((await cborToText(cborBinary)));
      setCborHex(cborToHex(cborBinary));
      setCborBinary(cborBinary);
      setJsonProperties((properties) => ({ size: getJsonBytesSize(jsonldDocument), name: properties?.name || '' }));
      setCborProperties({ size: cborBinary.byteLength, name: fileName });
      setStepHandle(2);
    } catch (e) {
      toast.error(String(e));
    }
  }

  const cborToJson = async () => {
    try {
      performance.mark(markerFrom);
      const jsonldDocument = await decode({ cborldBytes: cborBinary, documentLoader});
      performance.mark(markerTo);
      const fileName = cborProperties?.name ? `${getClearFileName(cborProperties.name)}.jsonld` : 'example';

      setJsonEdited(false);
      setAllStepsFinished(false);
      setJsonText(jsonToText(jsonldDocument));
      setJsonProperties({ size: getJsonBytesSize(jsonldDocument), name: fileName })
      setStepHandle(2);
    } catch (e) {
      toast.error(String(e));
    }
  }

  return (<>
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, 1fr)',
        gridGap: 30
      }}
    >
      <JsonPanel
        content={jsonText}
        fileProperties={jsonProperties}
        onFileLoad={initJson}
        onFileEdit={() => setJsonEdited(true)}
        onConversionClick={jsonToCbor}
      />
      <CborPanel
        text={cborText}
        hex={cborHex}
        binary={cborBinary}
        fileProperties={cborProperties}
        onFileLoad={initCbor}
        onConversionClick={cborToJson}
      />
    </Box>

    <div>
      {conversionRatio && <Typography sx={{
        fontWeight: '500',
        fontSize: 'h6.fontSize'
      }}>
        CBOR-LD file has {conversionRatio}% size of JSON-LD file
      </Typography>}
      {conversionTime && <Typography sx={{
        fontWeight: '500',
        fontSize: 'h6.fontSize',
        mt: '10px'
      }}>
        Last conversion time: {conversionTime}s
      </Typography>}
    </div>
  </>)
};
